/**
 * Emit hosts for the `sign-up` analytics group — 4 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { pushGtmDataLayerEvent } from '@/lib/analytics/pushGtmDataLayerEvent';
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type AuthenticationProvider } from '@/models/analytics/features/onboarding';
import { getUtmParams } from '@/utils/utmTracking';
import {
  SegmentActionName,
  SegmentObjectName,
  trackScreenViewed,
} from '@heidi/analytics/events';
import { ScreenName } from '@heidi/analytics/vocabulary';
import { OnboardingStep } from '@heidi/growth/contracts';

export function signUpFlowStarted() {
  trackLegacyEvent({
    name: SegmentObjectName.SignUp,
    props: {
      action: SegmentActionName.SignUpStarted,
    },
  });

  track({
    event_name: TrackingEventName.OnboardingStarted,
  });

  // V2 — user_started
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.UserStarted,
    props: {
      source: 'sign_up',
    },
  });

  trackScreenViewed({
    screen_name: ScreenName.GettingStartedOptions,
  });
}

export function signUpPhoneNumberChanged(input: {
  country: string;
  phoneNumber: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.SignUp,
    props: {
      action: SegmentActionName.SignUpPhoneNumberChanged,
      country: input.country,
      phoneNumber: input.phoneNumber,
    },
  });

  // V2 — user_updated
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.UserUpdated,
    props: {
      update_action: 'phone_number_changed',
      country: input.country,
    },
  });
}

export function trackSignUpFormCompleted(input: {
  authenticationProvider: AuthenticationProvider;
}) {
  track({
    event_name: TrackingEventName.OnboardingStepCompleted,
    props: {
      step: OnboardingStep.SIGN_UP_FORM,
      authentication_provider: input.authenticationProvider,
    },
  });
}

export function trackSignupSuccessful(input: {
  baaRequired?: boolean;
  complianceKitRequired?: boolean;
  phoneNumberProvided?: boolean;
}) {
  pushGtmDataLayerEvent({ event: 'signup_successful' });

  // Preserved exactly: custom_utm_source has always been the current URL's
  // utm_source.
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');

  // Additive: forward the full attribution set (utm_*, gclid, fbclid,
  // msclkid, …) as new properties so the Segment sign_up_successful event
  // also carries the ad-platform click IDs. Enhanced tracking reads from
  // sessionStorage so the IDs survive the OAuth redirect that strips them
  // from the URL. Mirrors demoBindOnboardingInfo.
  const attributionParams = getUtmParams();

  trackLegacyEvent({
    name: SegmentObjectName.SignUp,
    props: {
      // Spread first so no new attribution key can clobber an existing prop.
      ...attributionParams,
      action: SegmentActionName.SignUpSuccessful,
      custom_utm_source: utmSource,
      baa_required: input.baaRequired,
      compliance_kit_required: input.complianceKitRequired,
      phone_number_provided: input.phoneNumberProvided,
    },
  });
}
